import { CreditCard } from 'data/shared/__autogenerated__/types/CreditCard';
import { CreditCardData } from 'data/shared/CreditCardData';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { formatSpendCategory } from '../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../data/shared/utils/formatEarnRate';
import { CreditCardRewardsType } from '../data/shared/CreditCardRewardsTypes';
import { findMatchingEarnRate } from './findMatchingEarnRate';
import { generateDisclaimers } from './generateDisclaimers';
import { shouldShowCategory } from 'data/shared/utils/categoryFilterUtils';
import { getPromotionalTypeShortFootnote } from '../data/shared/promotionalTypes/promotionalTypeDisplayConfig';

type CategoryEarnRates = Record<
  CategoryType,
  {
    cardsWithCredits: EarnRateInfo[];
    sortedCardsByEarnRate: EarnRateInfo[];
  }
>;

type CategoryInfo = {
  category: string;
  earnRate: number;
  totalValue: number;
  rewardsType: string;
  disclaimers: string[];
  /** Footnote text when short disclaimer uses * (e.g. Bilt spend ratio) */
  footnote?: string;
};

type GroupedRow = {
  categories: CategoryInfo[];
  cardName: string;
  card: CreditCard;
  cardIndex: number;
  annualFee?: number;
};

/**
 * Generates Reddit markdown table for sharing a wallet's earn rates.
 * Uses the same data source as the UI (useCategoryEarnRatesAndCreditsV2).
 */
export function generateRedditMarkdown(
  categoryEarnRates: Partial<CategoryEarnRates>,
  shareUrl: string,
  selectedCards?: CreditCard[]
): string {
  // Collect best card for each category
  const categoryBestCards: Array<{
    category: string;
    earnRate: number;
    rewardsType: string;
    card: CreditCard;
    cardIndex?: number;
    cardName: string;
    annualFee?: number;
    earnRateInfo: EarnRateInfo;
  }> = [];

  Object.entries(categoryEarnRates).forEach(([category, data]) => {
    if (!data?.sortedCardsByEarnRate?.length) {
      return;
    }

    const bestCard = data.sortedCardsByEarnRate[0];
    if (!bestCard || bestCard.earnRate <= 1) {
      return;
    }

    // Apply the same filtering logic as the UI
    if (!shouldShowCategory(category, categoryEarnRates)) {
      return;
    }

    const cardData = CreditCardData[bestCard.card];
    if (!cardData) {
      return;
    }

    categoryBestCards.push({
      category,
      earnRate: bestCard.earnRate,
      rewardsType: bestCard.rewardsType,
      card: bestCard.card,
      cardIndex: bestCard.cardIndex,
      cardName: cardData.name,
      annualFee: cardData.annualFee,
      earnRateInfo: bestCard,
    });
  });

  if (categoryBestCards.length === 0) {
    return `[Wallet overview by CardClues](${shareUrl})\n\nNo bonus categories configured.`;
  }

  // Group categories by card instance (card + cardIndex to distinguish duplicates)
  const groupedByCard = new Map<string, GroupedRow>();

  categoryBestCards.forEach((item) => {
    // Find the matching raw earn rate to get disclaimers
    const matched = findMatchingEarnRate(item.earnRateInfo);
    const promotionalType = item.earnRateInfo.promotionalType;
    const disclaimers = matched
      ? generateDisclaimers(matched.rate, {
          compact: true,
          promotionalType,
        })
      : [];
    const footnote = promotionalType
      ? getPromotionalTypeShortFootnote(promotionalType)
      : undefined;

    const categoryInfo: CategoryInfo = {
      category: item.category,
      earnRate: item.earnRate,
      totalValue: item.earnRateInfo.totalValue,
      rewardsType: item.rewardsType,
      disclaimers,
      footnote,
    };

    const groupKey = `${item.card}_${item.cardIndex ?? 0}`;
    const existing = groupedByCard.get(groupKey);
    if (existing) {
      existing.categories.push(categoryInfo);
    } else {
      groupedByCard.set(groupKey, {
        categories: [categoryInfo],
        cardName: item.cardName,
        card: item.card,
        cardIndex: item.cardIndex ?? 0,
        annualFee: item.annualFee,
      });
    }
  });

  // Convert to array and calculate total value for each card
  const rows = Array.from(groupedByCard.values()).map((row) => {
    // Sort categories within each row: all_purchases first, then by earn rate descending
    row.categories.sort((a, b) => {
      const aIsAllPurchases =
        a.category === 'all_purchases' || a.category === 'allPurchases';
      const bIsAllPurchases =
        b.category === 'all_purchases' || b.category === 'allPurchases';
      if (aIsAllPurchases && !bIsAllPurchases) {
        return -1;
      }
      if (!aIsAllPurchases && bIsAllPurchases) {
        return 1;
      }
      return b.earnRate - a.earnRate;
    });
    return {
      ...row,
      // Sum of totalValue across all categories where this card is best
      totalValueSum: row.categories.reduce((sum, c) => sum + c.totalValue, 0),
    };
  });

  // Sort rows by total value sum (highest first), then by wallet order (cardIndex)
  rows.sort((a, b) => {
    if (b.totalValueSum !== a.totalValueSum) {
      return b.totalValueSum - a.totalValueSum;
    }
    return a.cardIndex - b.cardIndex;
  });

  // Add cards from the wallet that don't win any category
  if (selectedCards) {
    const cardsWithCategories = new Set(rows.map((r) => r.card));
    selectedCards.forEach((cardId) => {
      if (cardsWithCategories.has(cardId)) {
        return;
      }
      const cardData = CreditCardData[cardId];
      if (!cardData) {
        return;
      }
      rows.push({
        categories: [],
        cardName: cardData.name,
        card: cardId,
        cardIndex: rows.length,
        annualFee: cardData.annualFee,
        totalValueSum: 0,
      });
    });
  }

  // Build markdown table
  const lines: string[] = [
    `[Wallet overview by CardClues](${shareUrl})`,
    '',
    '| Card | Categories | Notes |',
    '|------|------------|-------|',
  ];

  rows.forEach((row) => {
    // Format categories with rates and disclaimers inline
    const formattedCategories =
      row.categories.length > 0
        ? row.categories
            .map((c) => {
              const name = formatSpendCategory(c.category);
              const rate = formatEarnRate(
                c.earnRate,
                c.rewardsType as CreditCardRewardsType
              );

              const rateWithDisclaimers =
                c.disclaimers.length > 0
                  ? `${rate}, ${c.disclaimers.join(', ')}`
                  : rate;

              return `**${name}** (${rateWithDisclaimers})`;
            })
            .join(', ')
        : '—';

    // Escape pipe characters in card names
    const escapedCardName = row.cardName.replace(/\|/g, '\\|');

    // Build notes (card-level info like annual fee)
    const notes: string[] = [];
    if (row.annualFee && row.annualFee > 0) {
      notes.push(`$${row.annualFee} AF`);
    }

    lines.push(`| ${escapedCardName} | ${formattedCategories} | ${notes.join(', ')} |`);
  });

  // Footer: superscript footnotes for short disclaimers (e.g. "Requires spend*")
  const footnotes = new Set<string>();
  rows.forEach((row) => {
    row.categories.forEach((c) => {
      if (c.footnote) {
        footnotes.add(c.footnote);
      }
    });
  });
  if (footnotes.size > 0) {
    lines.push('');
    footnotes.forEach((text) => lines.push(text));
  }

  lines.push('');
  lines.push('*Generated with [CardClues](https://cardclues.com)*');

  return lines.join('\n');
}
